import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Button from '../../components/ui/Button';
import WizardProgress from '../site-setup-wizard/components/WizardProgress';
import { setupService } from '../../services/setupService';
import { companyService } from '../../services/companyService';
import { authService } from '../../services/authService';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { sanitizeError } from '../../utils/sanitizeError';

// Module-level promise used as a mutex so concurrent mounts (e.g. React StrictMode)
// don't both create a placeholder company at the same time.
let creatingCompanyPromise = null;

const SetupFlowCompanyDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [companyId, setCompanyId] = useState(null);
  const [formData, setFormData] = useState({
    companyName: '',
    country: ''
  });
  const [errors, setErrors] = useState({});
  const emailVerificationPending = location?.state?.emailVerificationPending || false;
  const pendingEmail = location?.state?.email || '';

  // Enterprise flow: prefer location state, fall back to sessionStorage (survives auth redirects)
  const storedEnterprisePlan = (() => {
    try {
      const raw = sessionStorage.getItem('enterprisePlan');
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  })();
  const enterpriseFlow = location?.state?.enterpriseFlow || !!storedEnterprisePlan;
  const enterprisePlan = location?.state?.enterprisePlan || storedEnterprisePlan;
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendStatus, setResendStatus] = useState(null); // 'sent' | 'error' | null

  const steps = enterpriseFlow
    ? [
        { id: 1, title: 'Company Details' },
        { id: 2, title: 'Payment' },
        { id: 3, title: 'Create First Site' },
        { id: 4, title: 'Invite Team' },
        { id: 5, title: 'Daily Site Checklist' },
      ]
    : [
        { id: 1, title: 'Company Details' },
        { id: 2, title: 'Choose Plan' },
        { id: 3, title: 'Create First Site' },
        { id: 4, title: 'Invite Team' },
        { id: 5, title: 'Daily Site Checklist' },
      ];

  const countryOptions = [
    { value: 'GB', label: 'United Kingdom' },
    { value: 'IE', label: 'Ireland' },
    { value: 'US', label: 'United States' },
    { value: 'CA', label: 'Canada' },
    { value: 'AU', label: 'Australia' },
    { value: 'NZ', label: 'New Zealand' }
  ];

  useEffect(() => {
    loadCompanyData();
  }, []);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown(prev => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const loadCompanyData = async () => {
    try {
      const { data, error } = await companyService?.getUserCompany();

      if (data) {
        setCompanyId(data?.id);

        // Load saved progress or existing data
        const savedProgress = setupService?.loadProgress();
        if (savedProgress?.currentStep === 1 && savedProgress?.formData) {
          setFormData(savedProgress?.formData);
        } else if (data?.company_name) {
          setFormData({
            companyName: data?.company_name || '',
            country: data?.country || ''
          });
        }
      } else {
        // Brand-new user arriving from email verification — no company exists yet.
        // Create a placeholder company record now so companyId is always valid
        // before the form is submitted.

        // If another mount is already creating the company (StrictMode double-fire),
        // wait for that to finish and reuse its result.
        if (creatingCompanyPromise) {
          console.log('[CompanyDetails] Creation already in-flight — waiting for existing promise');
          const existingId = await creatingCompanyPromise;
          if (existingId) {
            setCompanyId(existingId);
            return;
          }
        }

        console.log('[CompanyDetails] No company found, creating placeholder for new user');
        const { data: { user } } = await supabase?.auth?.getUser();
        if (user?.id) {
          const doCreate = async () => {
            // Re-check right before insert — catches navigation-back where a
            // company was created on a prior visit but wasn't in state yet.
            const { data: recheck } = await companyService?.getUserCompany();
            if (recheck?.id) {
              console.log('[CompanyDetails] Company found on re-check, skipping create, id=', recheck?.id);
              return recheck?.id;
            }

            // Retry up to 3 times — the user_profiles row may not exist yet
            // (trigger propagation delay), causing an FK error on first attempt.
            let newCompany = null;
            let createError = null;
            for (let attempt = 0; attempt < 3; attempt++) {
              const result = await companyService?.createCompany(
                {
                  companyName: 'My Company',
                  registrationNumber: null,
                  addressLine1: 'To be updated',
                  addressLine2: null,
                  city: 'To be updated',
                  county: null,
                  postcode: 'TBD',
                  contactPhone: '00000000000',
                  contactEmail: user?.email || 'noreply@example.com'
                },
                user?.id
              );
              newCompany = result?.data;
              createError = result?.error;
              if (!createError) break;
              console.warn(`[CompanyDetails] createCompany attempt ${attempt + 1} failed:`, createError?.message);
              if (attempt < 2) await new Promise(r => setTimeout(r, 1500));
            }
            if (createError) {
              console.error('[CompanyDetails] Failed to create placeholder company after retries:', createError?.message);
              // Creation failed — try to fetch in case it was a duplicate/race
              const { data: fallback } = await companyService?.getUserCompany();
              return fallback?.id || null;
            }
            const newId = typeof newCompany === 'string' ? newCompany : newCompany?.id;
            console.log('[CompanyDetails] Placeholder company created, id=', newId);
            return newId;
          };

          creatingCompanyPromise = doCreate();
          const resolvedId = await creatingCompanyPromise;
          creatingCompanyPromise = null;
          if (resolvedId) setCompanyId(resolvedId);
        } else {
          console.error('[CompanyDetails] No authenticated user found during loadCompanyData');
        }
      }
    } catch (err) {
      console.error('Failed to load company:', err);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.companyName?.trim()) {
      newErrors.companyName = 'Company name is required';
    } else if (formData?.companyName?.trim()?.length < 2) {
      newErrors.companyName = 'Company name must be at least 2 characters';
    }

    if (!formData?.country) {
      newErrors.country = 'Country is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error for this field
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleCountryChange = (value) => {
    setFormData(prev => ({ ...prev, country: value }));
    if (errors?.country) {
      setErrors(prev => ({ ...prev, country: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      let resolvedCompanyId = companyId;

      // Safety net: if companyId is still null (e.g. placeholder creation failed),
      // attempt to fetch or create the company one more time before updating.
      if (!resolvedCompanyId) {
        const { data: existingCompany } = await companyService?.getUserCompany();
        if (existingCompany?.id) {
          resolvedCompanyId = existingCompany?.id;
          setCompanyId(resolvedCompanyId);
        } else {
          const { data: { user } } = await supabase?.auth?.getUser();
          if (!user?.id) throw new Error('Not authenticated — please refresh and try again.');
          const { data: newCompany, error: createError } = await companyService?.createCompany(
            {
              companyName: formData?.companyName,
              registrationNumber: null,
              addressLine1: 'To be updated',
              addressLine2: null,
              city: 'To be updated',
              county: null,
              postcode: 'TBD',
              contactPhone: '00000000000',
              contactEmail: user?.email || 'noreply@example.com'
            },
            user?.id
          );
          if (createError) throw new Error(sanitizeError(createError?.message, 'Failed to create company'));
          resolvedCompanyId = typeof newCompany === 'string' ? newCompany : newCompany?.id;
          setCompanyId(resolvedCompanyId);
        }
      }

      const { error } = await setupService?.updateCompanyDetails(resolvedCompanyId, formData);
      if (error) throw error;

      // Save progress
      setupService?.saveProgress(1, formData);

      // Single unified flow: always go to plan/calculator page
      navigate('/setup-flow-plan-confirmation');
    } catch (err) {
      setErrors({ submit: sanitizeError(err?.message, 'Failed to save company details') });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendVerification = async () => {
    if (!pendingEmail || resendCooldown > 0) return;
    setResendStatus(null);
    const { error } = await authService?.resendVerificationEmail(pendingEmail);
    if (error) {
      console.error('[SetupFlowCompanyDetails] resend verification failed:', { status: error?.status, code: error?.code, message: error?.message });
      const isRateLimited = error?.status === 429 ||
        error?.message?.toLowerCase()?.includes('rate limit') ||
        error?.message?.toLowerCase()?.includes('too many');
      setResendStatus(isRateLimited ? 'rate_limited' : 'error');
    } else {
      setResendStatus('sent');
      setResendCooldown(30);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-4 py-8 md:py-12">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">Welcome to SiteProof</h1>
          <p className="text-muted-foreground">Let's get your account set up</p>
        </div>

        <WizardProgress currentStep={1} totalSteps={5} steps={steps} onExit={() => navigate('/')} />

        {/* Email verification pending notice — shown when user arrives directly from registration */}
        {emailVerificationPending && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
            <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-amber-800 mb-1">Please verify your email address</p>
              <p className="text-sm text-amber-700 mb-1">
                We've sent a verification link to{' '}
                <strong>{pendingEmail || 'your email address'}</strong>.
              </p>
              <p className="text-sm text-amber-700 mb-3">
                If you don't see it, check your spam or junk folder. It usually arrives within 30 seconds.
              </p>
              {resendStatus === 'sent' && (
                <p className="text-sm font-medium text-green-700 mb-2">✓ Verification email sent again.</p>
              )}
              {resendStatus === 'rate_limited' && (
                <p className="text-sm text-red-600 mb-2">Too many emails sent recently. Please wait a few minutes before trying again.</p>
              )}
              {resendStatus === 'error' && (
                <p className="text-sm text-red-600 mb-2">Failed to resend. Please try again shortly.</p>
              )}
              <button
                type="button"
                onClick={handleResendVerification}
                disabled={resendCooldown > 0 || !pendingEmail}
                className="text-sm font-medium text-amber-800 underline underline-offset-2 hover:text-amber-900 disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline transition-opacity"
              >
                {resendCooldown > 0
                  ? `Resend available in ${resendCooldown}s`
                  : 'Resend verification email'}
              </button>
            </div>
          </div>
        )}

        <div className="bg-card rounded-lg shadow-elevation-1 p-6 md:p-8">
          <h2 className="text-2xl font-semibold text-foreground mb-6">Company Details</h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            <Input
              label="Company Name"
              name="companyName"
              value={formData?.companyName}
              onChange={handleChange}
              error={errors?.companyName}
              required
              placeholder="Enter your company name"
            />

            <Select
              label="Country"
              name="country"
              value={formData?.country}
              onChange={handleCountryChange}
              options={countryOptions}
              error={errors?.country}
              required
              placeholder="Select your country"
              searchable
            />

            {errors?.submit && (
              <div className="p-4 bg-destructive/10 border border-destructive rounded-md">
                <p className="text-sm text-destructive">{errors?.submit}</p>
              </div>
            )}

            <div className="flex justify-end pt-4">
              <Button
                type="submit"
                loading={isSubmitting}
                disabled={isSubmitting}
                size="lg"
              >
                {enterpriseFlow ? 'Continue to Payment' : 'Continue to Plan Selection'}
              </Button>
            </div>
          </form>
        </div>

        <div className="mt-6 text-center">
          <p className="text-sm text-muted-foreground">
            Step 1 of 5 - This setup will only take a few minutes
          </p>
        </div>
      </div>
    </div>
  );
};

export default SetupFlowCompanyDetails;